import { Minus as MinusIcon } from 'react-feather'
import { colors } from '@material-ui/core'
import {
  difference,
  filter,
  flattenDeep,
  fromPairs,
  isEmpty,
  map,
  unionBy,
} from 'lodash'

import theme from 'theme'
import constants from 'constants/index'
import palette from 'helpers/palette'

const getStyleTokens = (watch) => {
  const fontFamily = watch('fontFamily') || 'Arial'

  const primaryColor = palette.validateColor(watch('primaryColor'))
  const secondaryColor = palette.validateColor(watch('secondaryColor'))
  const calculatedSecondaryColor = palette.calculateColors(secondaryColor)
  const secondaryBGColor = palette.hexToRgba(secondaryColor, 0.06)

  return {
    fontFamily,
    primaryColor,
    secondaryColor,
    calculatedSecondaryColor,
    secondaryBGColor,
  }
}

const chipStatusColor = (status) => {
  return (
    (status && constants.cookies.banner.STATUS_CHIP_COLOR[status]) ||
    colors.grey[300]
  )
}

const chipFontStatusColor = (status) => {
  return (
    (status && constants.cookies.banner.STATUS_CHIP_FONT_COLOR[status]) ||
    colors.grey[800]
  )
}

const chipStatusLabel = (status) => {
  return (
    (status && constants.cookies.banner.STATUS_TRANSLATION[status]) ||
    'indeferido'
  ).toUpperCase()
}

const scannerStatusLabel = (status) => {
  return (
    (status && constants.cookies.banner.SCANNER_STATUS_TRANSLATION[status]) ||
    'indeferido'
  ).toUpperCase()
}

const buildStatusLabel = (status) => {
  return (
    (status && constants.cookies.build.BUILD_STATUS_TRANSLATION[status]) ||
    'indeferido'
  ).toUpperCase()
}

const scannerStatusColor = (status) => {
  return (
    (status && constants.cookies.banner.SCANNER_STATUS_COLOR[status]) ||
    theme.palette.custom.dark
  )
}

const buildCookieStats = (cookies) => {
  if (!cookies || cookies.length === 0) {
    return { total: 0, classified: 0, unknown: 0 }
  }

  const total = cookies.length
  const unknown = cookies.filter(
    (cookie) =>
      !cookie.category?.id ||
      cookie.category.id === constants.cookies.category.UNKNOWN_ID,
  ).length
  const classified = total - unknown

  return { total, classified, unknown }
}

const scannerStatusIcon = (status) => {
  return (
    (status && constants.cookies.banner.SCANNER_STATUS_ICON[status]) ||
    MinusIcon
  )
}

const mergeDescriptions = (bannerDescriptions) =>
  unionBy(
    bannerDescriptions,
    constants.cookies.banner.DEFAULT_DESCRIPTIONS,
    (desc) => desc.language.code,
  )

/**
 * Prepara os dados iniciais do formulário
 */

const extractCookiesFromBanner = (banner) => {
  const UNKNOWN_ID = constants.cookies.category.UNKNOWN_ID

  if (!banner?.classification) return []

  return (
    flattenDeep(
      map(banner.classification, ({ cookies, name, id }) =>
        map(cookies, (cookie) => ({
          ...cookie,
          platform: cookie.platform ?? '',
          category: { id, name },
        })),
      ),
    ) || []
  ).sort((a, b) => {
    if (a.category?.id === UNKNOWN_ID && b.category?.id !== UNKNOWN_ID)
      return -1
    if (a.category?.id !== UNKNOWN_ID && b.category?.id === UNKNOWN_ID) return 1
    return 0
  })
}

/**
 * Sinal inicial ao Google por categoria, no formato do formulário
 */
const buildConsentDefaultSignals = (grantedCategories) => {
  const {
    CONSENT_DEFAULT_CATEGORIES,
    CONSENT_DEFAULT_DENIED,
    CONSENT_DEFAULT_GRANTED,
  } = constants.cookies.category

  return fromPairs(
    map(CONSENT_DEFAULT_CATEGORIES, (category) => [
      category,
      grantedCategories?.includes(category)
        ? CONSENT_DEFAULT_GRANTED
        : CONSENT_DEFAULT_DENIED,
    ]),
  )
}

/**
 * Categorias com sinal inicial concedido, no formato da API
 */
const buildConsentDefaultGrantedCategories = (consentDefaultSignals) => {
  const { CONSENT_DEFAULT_CATEGORIES, CONSENT_DEFAULT_GRANTED } =
    constants.cookies.category

  return filter(
    CONSENT_DEFAULT_CATEGORIES,
    (category) => consentDefaultSignals?.[category] === CONSENT_DEFAULT_GRANTED,
  )
}

const prepareFormDefaultValues = (banner) => {
  const { AUTO_BLOCK_FIXED_CATEGORIES, UNKNOWN_KEY } =
    constants.cookies.category

  const cookiesArray = extractCookiesFromBanner(banner)
  const savedAutoBlockCategories = banner?.autoBlockEnabled
    ? banner?.autoBlockCategories || []
    : []

  return {
    name: banner?.name || '',
    urls: banner?.urls?.length ? banner.urls : [{ url: '', id: null }],
    cookies: cookiesArray,
    primaryColor: banner?.primaryColor || theme.palette.primary.main,
    secondaryColor: banner?.secondaryColor || theme.palette.primary.hover,
    logo: undefined,
    position: banner?.position || constants.cookies.banner.BANNER_LEFT_POSITION,
    hideOnAccept: banner?.hideOnAccept || false,
    hideRejectButton: banner?.hideRejectButton || false,
    privacyPortalEnabled: banner?.privacyPortalEnabled ?? true,
    privacyPortalUrl: banner?.privacyPortalUrl || '',
    fontFamily: banner?.fontFamily || '',
    isCompact: banner?.isCompact || false,
    ageGateEnabled: false,
    ageGateMinimumAge: 18,
    ageGateOverlayOpacity: 50,
    autoBlockCategorized: isEmpty(
      difference(AUTO_BLOCK_FIXED_CATEGORIES, savedAutoBlockCategories),
    ),
    autoBlockUnknown: savedAutoBlockCategories.includes(UNKNOWN_KEY),
    consentDefaultSignals: buildConsentDefaultSignals(
      banner?.consentDefaultGrantedCategories,
    ),
  }
}

/**
 * Verifica se o banner está em processo de compilação
 */
const isBannerCompiling = (banner) => {
  return (
    banner?.build?.status === constants.cookies.build.INITIATED_BUILD_STATUS_ID
  )
}

/**
 * Verifica se o age gate está em processo de compilação
 */
const isAgeGateCompiling = (ageGate) => {
  return (
    ageGate?.build?.status === constants.cookies.build.INITIATED_BUILD_STATUS_ID
  )
}

/**
 * Verifica se a compilação foi completada
 */
const isBuildCompleted = (banner) => {
  return (
    banner?.build?.status === constants.cookies.build.COMPLETED_BUILD_STATUS_ID
  )
}

/**
 * Verifica se o scanner do banner está em andamento
 */
const isBannerScanning = (banner) => {
  return (
    banner?.scanner?.status ===
    constants.cookies.banner.INITIATED_SCANNER_STATUS_ID
  )
}

/**
 * Obtém o texto do botão baseado no step atual
 */
const getButtonText = (activeStep, banner, isCompiling) => {
  const { STEPS, BUTTON_TEXTS } = constants.cookies.banner
  if (activeStep === STEPS.INSTALLATION) {
    const buildCompleted = isBuildCompleted(banner)
    if (buildCompleted) return BUTTON_TEXTS[STEPS.INSTALLATION].CONTINUE
    return isCompiling
      ? BUTTON_TEXTS[STEPS.INSTALLATION].COMPILING
      : BUTTON_TEXTS[STEPS.INSTALLATION].COMPILE
  }

  return BUTTON_TEXTS[activeStep] || BUTTON_TEXTS.DEFAULT
}

/**
 * Obtém o tooltip de status do banner
 */
const getStatusTooltip = (status) => {
  const tooltips = {
    active: 'Banner configurado e pronto para utilização!',
    incomplete:
      'Banner pode ser utilizado mas possui cookies classificados como "outros"',
    inactive: 'Banner desativado e não está sendo exibido no site',
  }
  return tooltips[status] || 'Status não definido'
}

const computeLeftGridSizes = ({
  positionPreview,
  isTextCustomizationLevel2Preview,
  logoPreview,
}) => {
  const xs = 12
  const md = positionPreview || isTextCustomizationLevel2Preview ? 12 : 5
  const lg =
    positionPreview || isTextCustomizationLevel2Preview
      ? 12
      : logoPreview
      ? 6
      : 5
  const xl = positionPreview ? 4 : isTextCustomizationLevel2Preview ? 12 : 6

  return { xs, md, lg, xl }
}

const computeRightGridSizes = ({
  positionPreview,
  isTextCustomizationLevel2Preview,
  logoPreview,
}) => {
  const xs = 12
  const md = positionPreview || isTextCustomizationLevel2Preview ? 12 : 7
  const lg =
    positionPreview || isTextCustomizationLevel2Preview
      ? 12
      : logoPreview
      ? 6
      : 7
  const xl = positionPreview ? 8 : isTextCustomizationLevel2Preview ? 12 : 6

  return { xs, md, lg, xl }
}

const status = {
  buildConsentDefaultGrantedCategories,
  buildCookieStats,
  buildStatusLabel,
  chipFontStatusColor,
  chipStatusColor,
  chipStatusLabel,
  computeLeftGridSizes,
  computeRightGridSizes,
  extractCookiesFromBanner,
  getButtonText,
  getStatusTooltip,
  getStyleTokens,
  isAgeGateCompiling,
  isBannerCompiling,
  isBannerScanning,
  mergeDescriptions,
  prepareFormDefaultValues,
  scannerStatusColor,
  scannerStatusIcon,
  scannerStatusLabel,
}

export default status
